import { DownOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Form, Input } from 'antd';
import { RadioAlert } from '@/components/radio-alert';
import { ToggleHeader, ToggleSwitch } from '@/components/toggle-switch';
import { cn } from '@/libs/utils';
import {
  HR_DOCUMENTS_TYPES_OPTIONS,
  SEPARATED_REQUEST_DOCS_OPTIONS,
} from '@/shared/company';
import { translateOptions } from '@/utils/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/** HR switch and its settings block. */
export default function CompanyHrSettings() {
  const {
    canManageDocsSettings,
    companyAdminsReceiveHREmailsKeys,
    companyAdminsReceiveHREmailsState,
    companyId,
    documentReturnTypeKeys,
    documentReturnTypeState,
    documentSeparateRequestResponseKeys,
    documentSeparateRequestResponseState,
    filteredHrDocumentsOptions,
    form,
    hrExpanded,
    isForEmployeesKeys,
    isForEmployeesState,
    isHrChecked,
    isHrKeys,
    isHrState,
    isProfile,
    isSickLeaveRequestKeys,
    isSickLeaveRequestState,
    ownershipStructureKeys,
    ownershipStructureState,
    sectionClass,
    setDocsSettingsOpen,
    setHrExpanded,
    shouldHrGroup,
    t,
    typeOfHrKeys,
    typeOfHrState,
    weekendLeavesKeys,
    weekendLeavesState,
  } = useCompanyGeneralDataForm();
  // On the profile the HR settings are laid out by
  // CompanyHrProfilePanel; only the hidden ownership field stays here.
  if (isProfile) {
    return (
      ownershipStructureState?.visible && (
        <Form.Item
          label={null}
          name={ownershipStructureKeys}
          className="hidden"
        >
          <Input
            type="hidden"
            name={ownershipStructureKeys}
            disabled={ownershipStructureState?.disabled}
          />
        </Form.Item>
      )
    );
  }

  return (
    <div className={sectionClass('hr')}>
      {isHrState?.visible && (
        <ToggleHeader
          title={t('hrPlatform', { ns: 'forms' })}
          className={cn(
            '',
            isHrChecked && shouldHrGroup && hrExpanded ? 'mb-5' : ''
          )}
        >
          <div className="flex items-center gap-3">
            {isHrChecked && shouldHrGroup && (
              <DownOutlined
                className={cn(
                  'cursor-pointer text-xs text-gray-400 transition-transform',
                  !hrExpanded && '-rotate-90'
                )}
                onClick={() => setHrExpanded((v) => !v)}
              />
            )}
            <ToggleSwitch
              name={isHrKeys}
              form={form}
              disabled={isHrState?.disabled}
              onChangeAction={(val) => val && setHrExpanded(true)}
            />
          </div>
        </ToggleHeader>
      )}

      {ownershipStructureState?.visible && (
        <Form.Item
          label={null}
          name={ownershipStructureKeys}
          className="hidden"
        >
          <Input
            type="hidden"
            name={ownershipStructureKeys}
            disabled={ownershipStructureState?.disabled}
          />
        </Form.Item>
      )}

      {shouldHrGroup ? (
        <>
          <div
            className={cn(
              isHrChecked && hrExpanded
                ? 'block rounded-md border bg-white p-4'
                : 'hidden'
            )}
          >
            {typeOfHrState?.visible && (
              <RadioAlert
                name={typeOfHrKeys}
                disabled={typeOfHrState?.disabled}
                label={t('hrDocuments', { ns: 'forms' })}
                alertText={t('hrDocumentsInfoMessage', {
                  ns: 'messages',
                })}
                options={translateOptions(filteredHrDocumentsOptions, t)}
              />
            )}

            {documentReturnTypeState?.visible && (
              <RadioAlert
                name={documentReturnTypeKeys}
                disabled={documentReturnTypeState?.disabled}
                label={t('hrDocumentsTypes', { ns: 'forms' })}
                alertText={t('hrDocumentsFormatInfoMessage', {
                  ns: 'messages',
                })}
                options={translateOptions(HR_DOCUMENTS_TYPES_OPTIONS, t)}
              />
            )}

            {documentSeparateRequestResponseState?.visible && (
              <RadioAlert
                name={documentSeparateRequestResponseKeys}
                disabled={documentSeparateRequestResponseState?.disabled}
                label={t('separatedRequestDocs', { ns: 'forms' })}
                alertText={t('separatedRequestDocsInfoMessage', {
                  ns: 'messages',
                })}
                options={translateOptions(SEPARATED_REQUEST_DOCS_OPTIONS, t)}
              />
            )}

            {/* Additional Docs Settings Button — HR doc config, hidden for
                      roles with no HR-document remit (e.g. eInvoiceOfficer). */}
            {companyId && canManageDocsSettings && (
              // Saves on its own, so it stays usable in the read-only view.
              <ConfigProvider componentDisabled={false}>
                <div className="my-4">
                  <Button
                    type="primary"
                    onClick={() => setDocsSettingsOpen(true)}
                    className="btn-primary"
                    icon={<SettingOutlined />}
                  >
                    {t('additionalDocsSettings', { ns: 'forms' })}
                  </Button>
                </div>
              </ConfigProvider>
            )}

            {weekendLeavesState?.visible && (
              <ToggleHeader
                title={t('weekendLeaves', { ns: 'forms' })}
                className="my-5"
              >
                <ToggleSwitch
                  name={weekendLeavesKeys}
                  form={form}
                  disabled={weekendLeavesState?.disabled}
                />
              </ToggleHeader>
            )}

            {isForEmployeesState?.visible && (
              <ToggleHeader
                title={t('isForEmployees', { ns: 'forms' })}
                className="mb-5"
              >
                <ToggleSwitch
                  name={isForEmployeesKeys}
                  form={form}
                  disabled={isForEmployeesState?.disabled}
                />
              </ToggleHeader>
            )}

            {companyAdminsReceiveHREmailsState?.visible && (
              <ToggleHeader
                title={t('companyAdminReceiveHREmails', {
                  ns: 'forms',
                })}
              >
                <ToggleSwitch
                  form={form}
                  name={companyAdminsReceiveHREmailsKeys}
                  disabled={companyAdminsReceiveHREmailsState?.disabled}
                />
              </ToggleHeader>
            )}

            {isSickLeaveRequestState?.visible && (
              <ToggleHeader
                title={t('isSickLeaveRequest', { ns: 'forms' })}
                className="mt-5"
              >
                <ToggleSwitch
                  name={isSickLeaveRequestKeys}
                  form={form}
                  disabled={isSickLeaveRequestState?.disabled}
                />
              </ToggleHeader>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
